import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { parseAsBoolean, parseAsInteger, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import type { Paginated } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { useTableParams } from '@/components/shared/use-table-params';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, errorMessage } from '@/lib/api';
import { fmtDate, maskPhone } from '@/lib/format';
import { useBarbers, useTimezone } from '@/lib/org';
import { useSession } from '@/lib/session';
import type { CustomerRow } from './api';
import { CustomerFormModal } from './customer-form';

const ALL = 'todos';

export function CustomersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const tz = useTimezone();
  const { can } = useSession();
  const barbers = useBarbers();
  const params = useTableParams({ sort: 'name' });
  const [filters, setFilters] = useQueryStates(
    {
      inativos: parseAsInteger,
      barbeiro: parseAsString,
      comunicacoes: parseAsStringLiteral(['sim', 'nao'] as const),
      excluidos: parseAsBoolean.withDefault(false),
    },
    { history: 'replace' },
  );
  const [editing, setEditing] = useState<CustomerRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const filterQuery = {
    inactiveDays: filters.inativos ?? undefined,
    barberId: filters.barbeiro ?? undefined,
    marketingConsent: filters.comunicacoes ? (filters.comunicacoes === 'sim' ? 'true' : 'false') : undefined,
    includeDeleted: filters.excluidos ? 'true' : undefined,
  };
  const query = { ...params.query, ...filterQuery };
  const list = useQuery({
    queryKey: ['customers', 'list', query],
    queryFn: () => api.get<Paginated<CustomerRow>>('/customers', query),
    placeholderData: (prev) => prev,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['customers'] });

  const restore = useMutation({
    mutationFn: (c: CustomerRow) => api.post(`/customers/${c.id}/restore`),
    onSuccess: () => {
      toast.success('Cliente restaurado');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const remove = async (c: CustomerRow) => {
    try {
      await api.del(`/customers/${c.id}`);
      toast.success('Cliente excluído');
      refresh();
    } catch (e) {
      toast.error(errorMessage(e));
      throw e;
    }
  };

  const exportXlsx = async () => {
    setExporting(true);
    try {
      await api.download('/customers/export', { q: params.query.q, sort: params.query.sort, order: params.query.order, ...filterQuery }, 'clientes.xlsx');
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const setFilter = (patch: Parameters<typeof setFilters>[0]) => {
    setFilters(patch);
    params.set({ page: 1 });
  };

  const columns: DataColumn<CustomerRow>[] = [
    {
      id: 'name',
      header: 'Cliente',
      meta: { sortKey: 'name' },
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{row.original.name}</p>
          {row.original.tags.length > 0 && <p className="truncate text-xs text-muted-foreground">{row.original.tags.join(' · ')}</p>}
        </div>
      ),
    },
    {
      id: 'contact',
      header: 'Contato',
      cell: ({ row }) => (
        <div className="min-w-0 text-sm">
          <p className="tabular">{row.original.phone ? maskPhone(row.original.phone) : <span className="text-muted-foreground">Sem telefone</span>}</p>
          {row.original.email && <p className="max-w-56 truncate text-xs text-muted-foreground">{row.original.email}</p>}
        </div>
      ),
    },
    { id: 'barber', header: 'Barbeiro preferido', cell: ({ row }) => row.original.preferredBarberName ?? <span className="text-muted-foreground">—</span> },
    {
      id: 'lastVisitAt',
      header: 'Última visita',
      meta: { sortKey: 'lastVisitAt' },
      cell: ({ row }) =>
        row.original.lastVisitAt ? <span className="tabular">{fmtDate(row.original.lastVisitAt, tz)}</span> : <span className="text-muted-foreground">Nunca veio</span>,
    },
    {
      id: 'consent',
      header: 'Comunicações',
      cell: ({ row }) =>
        row.original.deletedAt ? (
          <Badge variant="outline">Excluído</Badge>
        ) : (
          <Badge variant={row.original.marketingConsent ? 'secondary' : 'outline'}>{row.original.marketingConsent ? 'Aceita' : 'Não aceita'}</Badge>
        ),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      meta: { align: 'right' },
      cell: ({ row }) => {
        const c = row.original;
        if (!can('customers.write')) return null;
        return (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            {c.deletedAt ? (
              <Button variant="outline" size="sm" onClick={() => restore.mutate(c)} disabled={restore.isPending}>
                <RotateCcw aria-hidden /> Restaurar
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Editar ${c.name}`}
                  onClick={() => {
                    setEditing(c);
                    setFormOpen(true);
                  }}
                >
                  <Pencil aria-hidden />
                </Button>
                <ConfirmDialog
                  trigger={
                    <Button variant="ghost" size="icon" aria-label={`Excluir ${c.name}`}>
                      <Trash2 className="text-destructive" aria-hidden />
                    </Button>
                  }
                  title={`Excluir ${c.name}?`}
                  description='O cliente sai das buscas e das campanhas, mas o histórico de atendimentos e pagamentos é mantido. Ele fica em "Excluídos" e pode ser restaurado.'
                  confirmLabel="Excluir cliente"
                  onConfirm={() => remove(c)}
                />
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Clientes"
        description="Quem frequenta a barbearia, com histórico, fidelidade e planos."
        actions={
          <>
            {can('customers.export') && (
              <Button variant="outline" onClick={exportXlsx} disabled={exporting}>
                <Download aria-hidden /> {exporting ? 'Gerando…' : 'Exportar planilha'}
              </Button>
            )}
            {can('customers.write') && (
              <Button
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                <Plus aria-hidden /> Novo cliente
              </Button>
            )}
          </>
        }
      />
      <DataTable
        columns={columns}
        data={list.data}
        params={params}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={() => list.refetch()}
        searchPlaceholder="Buscar por nome, telefone ou e-mail"
        onRowClick={(c) => navigate(`/app/clientes/${c.id}`)}
        empty={{
          title: 'Nenhum cliente cadastrado',
          description: 'Cadastre o primeiro cliente ou crie-o direto pela agenda.',
          action: can('customers.write') ? (
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              <Plus aria-hidden /> Novo cliente
            </Button>
          ) : undefined,
        }}
        filters={
          <>
            <Select value={filters.inativos ? String(filters.inativos) : ALL} onValueChange={(v) => setFilter({ inativos: v === ALL ? null : Number(v) })}>
              <SelectTrigger className="w-44" aria-label="Filtrar por inatividade">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Qualquer frequência</SelectItem>
                {[30, 60, 90].map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    Sem vir há {n} dias
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.barbeiro ?? ALL} onValueChange={(v) => setFilter({ barbeiro: v === ALL ? null : v })}>
              <SelectTrigger className="w-44" aria-label="Filtrar por barbeiro preferido">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos os barbeiros</SelectItem>
                {barbers.data?.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.comunicacoes ?? ALL} onValueChange={(v) => setFilter({ comunicacoes: v === ALL ? null : (v as 'sim' | 'nao') })}>
              <SelectTrigger className="w-48" aria-label="Filtrar por aceite de comunicações">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Aceita comunicações: todos</SelectItem>
                <SelectItem value="sim">Aceita comunicações</SelectItem>
                <SelectItem value="nao">Não aceita</SelectItem>
              </SelectContent>
            </Select>
            <Button variant={filters.excluidos ? 'secondary' : 'ghost'} size="sm" aria-pressed={filters.excluidos} onClick={() => setFilter({ excluidos: !filters.excluidos })}>
              {filters.excluidos ? 'Mostrando excluídos' : 'Ver excluídos'}
            </Button>
          </>
        }
      />
      <CustomerFormModal open={formOpen} onOpenChange={setFormOpen} customer={editing} />
    </div>
  );
}
